<template>
    <div
         ref="blockRef"
         :style="blockStyle"
         @click="onClick"
         @mousedown="onMoveStart"
         class="text-block-canvas"
         :class="{ hoverBorder: !isActive, activeBlock: isActive && !contentEditable, editingBlock: isActive && contentEditable }"
    >
        <div
            ref="textRef"
            class="text-content"
            :contenteditable="contentEditable && isActive"
            @dblclick.stop="editModeOn"
            @focusout="editModeOff"
            @input="onInput"
            @keydown="onKeydown"
            :style="textStyle"
        >{{ displayText }}</div>

        <template v-if="isActive && !contentEditable">
            <div class="resize-handle handle-tl" @mousedown.stop="onResizeStart($event, 'tl')"></div>
            <div class="resize-handle handle-tr" @mousedown.stop="onResizeStart($event, 'tr')"></div>
            <div class="resize-handle handle-bl" @mousedown.stop="onResizeStart($event, 'bl')"></div>
            <div class="resize-handle handle-br" @mousedown.stop="onResizeStart($event, 'br')"></div>
            <div class="resize-handle handle-t"  @mousedown.stop="onResizeStart($event, 't')"></div>
            <div class="resize-handle handle-b"  @mousedown.stop="onResizeStart($event, 'b')"></div>
            <div class="resize-handle handle-l"  @mousedown.stop="onResizeStart($event, 'l')"></div>
            <div class="resize-handle handle-r"  @mousedown.stop="onResizeStart($event, 'r')"></div>
        </template>

        <div
            v-if="showDropdown && filtered.length > 0"
            class="wildcard-dropdown"
            :style="dropdownStyle"
        >
            <div
                v-for="(item, idx) in filtered"
                :key="item.value"
                :class="['wildcard-item', { highlighted: idx === highlightedIndex }]"
                @mousedown.prevent="selectWildcard(item)"
                @mouseenter="highlightedIndex = idx"
            >
                <span class="wildcard-value">%{{ item.value }}%</span>
                <span class="wildcard-label" v-if="item.label">{{ item.label }}</span>
            </div>
        </div>
    </div>
</template>
<script setup>

import {ref, computed, watch, onBeforeUnmount, defineProps, defineEmits} from "vue"

import { replaceWildcards } from "@/libs/wildcards";
import { normalizeTextBlockText } from "@/libs/text-block-text";

const props = defineProps(['modelValue', 'activeBlockId', 'zoom', 'number', 'wildcards', 'allBlocks', 'templateWidth', 'dataSet'])

const emit = defineEmits([
    'update:modelValue',
    'activateBlock',
    'moveBlock',
    'editBlockEnabled',
    'textEditCommitted',
    'interactionStart',
    'interactionEnd',
    'updateSnapLines'
]);
const block = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
})

const blockRef = ref(null);
const textRef = ref(null);
const contentEditable = ref(false);

const displayText = computed(() => {
    if (!props.dataSet || contentEditable.value) return normalizeTextBlockText(block.value.text);
    return normalizeTextBlockText(replaceWildcards(block.value.text, props.dataSet));
});
const editModeOn = () => {
    contentEditable.value = true;
    emit('editBlockEnabled');
}
const editModeOff = (e) => {
    showDropdown.value = false;
    contentEditable.value = false;
    if (e && e.target) {
        block.value.text = e.target.innerText;
        emit('update:modelValue', block.value);
        emit('textEditCommitted');
    }
}
const pixelsInSm = 3.79;
const mmToPixel = (mm) => Math.round(mm * pixelsInSm * props.zoom);
const pixelToMM = (pixels) => pixels / (pixelsInSm * props.zoom);

// --- Wildcard autocomplete ---
const showDropdown = ref(false);
const highlightedIndex = ref(0);
const searchQuery = ref('');
const dropdownStyle = ref({});

const filtered = computed(() => {
    if (!props.wildcards) return [];
    const q = searchQuery.value.toLowerCase();
    return props.wildcards.filter(w =>
        w.value.toLowerCase().includes(q) ||
        (w.label && w.label.toLowerCase().includes(q))
    );
});

watch(filtered, () => {
    highlightedIndex.value = 0;
});

function getCaretInfo() {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    const el = textRef.value;
    if (!el || !el.contains(range.startContainer)) return null;

    // Get text before cursor
    const preRange = document.createRange();
    preRange.setStart(el, 0);
    preRange.setEnd(range.startContainer, range.startOffset);
    const textBefore = preRange.toString();

    // Get caret position for dropdown placement
    const rect = range.getBoundingClientRect();
    const blockRect = blockRef.value.getBoundingClientRect();

    return {
        textBefore,
        caretLeft: rect.left - blockRect.left,
        caretTop: rect.bottom - blockRect.top
    };
}

function checkForWildcardTrigger() {
    const info = getCaretInfo();
    if (!info) { showDropdown.value = false; return; }

    const lastPercent = info.textBefore.lastIndexOf('%');
    if (lastPercent === -1) { showDropdown.value = false; return; }

    const afterPercent = info.textBefore.substring(lastPercent + 1);
    if (afterPercent.includes('%')) { showDropdown.value = false; return; }

    searchQuery.value = afterPercent;
    dropdownStyle.value = {
        left: Math.max(0, info.caretLeft) + 'px',
        top: info.caretTop + 2 + 'px'
    };
    showDropdown.value = true;
}

function selectWildcard(item) {
    const el = textRef.value;
    if (!el) return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    // Get text content and find the trigger %
    const preRange = document.createRange();
    preRange.setStart(el, 0);
    preRange.setEnd(range.startContainer, range.startOffset);
    const textBefore = preRange.toString();
    const lastPercent = textBefore.lastIndexOf('%');

    if (lastPercent === -1) return;

    // Walk through text nodes to find the position of the trigger %
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    let charCount = 0;
    let startNode = null, startOffset = 0;

    while (walker.nextNode()) {
        const node = walker.currentNode;
        const nodeLen = node.textContent.length;
        if (startNode === null && charCount + nodeLen > lastPercent) {
            startNode = node;
            startOffset = lastPercent - charCount;
        }
        charCount += nodeLen;
    }

    if (!startNode) return;

    // Create range from % to current cursor
    const deleteRange = document.createRange();
    deleteRange.setStart(startNode, startOffset);
    deleteRange.setEnd(range.startContainer, range.startOffset);
    deleteRange.deleteContents();

    // Insert the wildcard text
    const wildcardText = '%' + item.value + '%';
    const textNode = document.createTextNode(wildcardText);
    deleteRange.insertNode(textNode);

    // Move cursor after inserted text
    const newRange = document.createRange();
    newRange.setStartAfter(textNode);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    showDropdown.value = false;
}

const onInput = () => {
    // Don't update reactive data during editing — it causes Vue to re-render
    // the contenteditable div and reset the cursor position.
    // Text is synced on focusout instead.
    checkForWildcardTrigger();
}

const onKeydown = (e) => {
    if (!showDropdown.value || filtered.value.length === 0) return;

    if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        highlightedIndex.value = (highlightedIndex.value + 1) % filtered.value.length;
    } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        highlightedIndex.value = (highlightedIndex.value - 1 + filtered.value.length) % filtered.value.length;
    } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        selectWildcard(filtered.value[highlightedIndex.value]);
    } else if (e.key === 'Escape') {
        e.preventDefault();
        showDropdown.value = false;
    }
}

// --- Snap guides ---
const SNAP_THRESHOLD = 3; // mm

function getSnapPositions() {
    const positions = [];
    // Page center
    if (props.templateWidth) {
        positions.push({ x: Math.round(props.templateWidth / 2), source: 'page-center' });
    }
    // Other blocks
    if (props.allBlocks) {
        for (const b of props.allBlocks) {
            if (b.id === block.value.id) continue;
            if (b.type !== 'text') continue;
            positions.push({ x: b.x, source: 'block-left' });
            positions.push({ x: Math.round(b.x + b.width / 2), source: 'block-center' });
            positions.push({ x: b.x + b.width, source: 'block-right' });
        }
    }
    return positions;
}

function applySnap(newX, width) {
    const snapPositions = getSnapPositions();
    const edges = [
        { offset: 0, name: 'left' },           // left edge
        { offset: Math.round(width / 2), name: 'center' },  // center
        { offset: width, name: 'right' },       // right edge
    ];
    let bestSnap = null;
    let bestDist = SNAP_THRESHOLD + 1;

    for (const edge of edges) {
        const edgePos = newX + edge.offset;
        for (const snap of snapPositions) {
            const dist = Math.abs(edgePos - snap.x);
            if (dist < bestDist) {
                bestDist = dist;
                bestSnap = { snapX: snap.x, edgeOffset: edge.offset };
            }
        }
    }

    if (bestSnap) {
        return {
            x: bestSnap.snapX - bestSnap.edgeOffset,
            lineX: bestSnap.snapX
        };
    }
    return { x: newX, lineX: null };
}

// --- Move via mousedown/mousemove/mouseup ---
const moving = ref(false);
const moveStart = ref({ x: 0, y: 0, blockX: 0, blockY: 0 });

const onMoveStart = (e) => {
    if (contentEditable.value) return;
    e.preventDefault();
    moving.value = true;
    moveStart.value = {
        x: e.clientX,
        y: e.clientY,
        blockX: block.value.x,
        blockY: block.value.y
    };
    emit('interactionStart');
    document.addEventListener('mousemove', onMoveMove);
    document.addEventListener('mouseup', onMoveEnd);
}

const onMoveMove = (e) => {
    if (!moving.value) return;
    const dx = pixelToMM(e.clientX - moveStart.value.x);
    const dy = pixelToMM(e.clientY - moveStart.value.y);
    const rawX = Math.max(0, Math.round(moveStart.value.blockX + dx));
    const newY = Math.max(0, Math.round(moveStart.value.blockY + dy));

    const snap = applySnap(rawX, block.value.width);
    block.value.x = snap.x;
    block.value.y = newY;
    emit('update:modelValue', block.value);
    emit('updateSnapLines', snap.lineX !== null ? [snap.lineX] : []);
}

const onMoveEnd = () => {
    moving.value = false;
    emit('updateSnapLines', []);
    emit('interactionEnd');
    document.removeEventListener('mousemove', onMoveMove);
    document.removeEventListener('mouseup', onMoveEnd);
}

// --- Resize via mousedown/mousemove/mouseup ---
const resizing = ref(false);
const resizeHandle = ref('');
const resizeStart = ref({ x: 0, y: 0, blockX: 0, blockY: 0, blockW: 0, blockH: 0 });

const onResizeStart = (e, handle) => {
    e.preventDefault();
    resizing.value = true;
    resizeHandle.value = handle;
    resizeStart.value = {
        x: e.clientX,
        y: e.clientY,
        blockX: block.value.x,
        blockY: block.value.y,
        blockW: block.value.width,
        blockH: block.value.height
    };
    emit('interactionStart');
    document.addEventListener('mousemove', onResizeMove);
    document.addEventListener('mouseup', onResizeEnd);
}

const MIN_WIDTH = 10;
const MIN_HEIGHT = 4;

const onResizeMove = (e) => {
    if (!resizing.value) return;
    const dx = pixelToMM(e.clientX - resizeStart.value.x);
    const dy = pixelToMM(e.clientY - resizeStart.value.y);
    const h = resizeHandle.value;
    const s = resizeStart.value;

    // Right edge
    if (h === 'r' || h === 'tr' || h === 'br') {
        block.value.width = Math.max(MIN_WIDTH, Math.round(s.blockW + dx));
    }
    // Left edge
    if (h === 'l' || h === 'tl' || h === 'bl') {
        const newW = Math.max(MIN_WIDTH, Math.round(s.blockW - dx));
        block.value.x = Math.max(0, Math.round(s.blockX + s.blockW - newW));
        block.value.width = newW;
    }
    // Bottom edge
    if (h === 'b' || h === 'bl' || h === 'br') {
        block.value.height = Math.max(MIN_HEIGHT, Math.round(s.blockH + dy));
    }
    // Top edge
    if (h === 't' || h === 'tl' || h === 'tr') {
        const newH = Math.max(MIN_HEIGHT, Math.round(s.blockH - dy));
        block.value.y = Math.max(0, Math.round(s.blockY + s.blockH - newH));
        block.value.height = newH;
    }

    emit('update:modelValue', block.value);
}

const onResizeEnd = () => {
    resizing.value = false;
    emit('interactionEnd');
    document.removeEventListener('mousemove', onResizeMove);
    document.removeEventListener('mouseup', onResizeEnd);
}

onBeforeUnmount(() => {
    document.removeEventListener('mousemove', onMoveMove);
    document.removeEventListener('mouseup', onMoveEnd);
    document.removeEventListener('mousemove', onResizeMove);
    document.removeEventListener('mouseup', onResizeEnd);
});

const isActive = computed(() => block.value.id === props.activeBlockId)
const onClick = (e) => {
    e.stopPropagation();
    if(!isActive.value) emit('activateBlock', block.value.id)
}

// Функция для получения правильного font-family с fallback
const getFontFamily = (fontName) => {
    const fontMap = {
        'Calibri': 'Calibri, "Segoe UI", sans-serif',
        'Times': '"Times New Roman", Times, serif',
        'Arial': 'Arial, Helvetica, sans-serif',
        'Spectral': 'Spectral, Georgia, serif',
        'Bannikova': 'Bannikova, Georgia, serif',
        'Minion Pro': '"Minion Pro", Georgia, serif',
        'Akzidenz Grotesk': '"Akzidenz Grotesk", Arial, sans-serif',
        'Google Sans': '"Google Sans", Arial, sans-serif',
        'Roboto': 'Roboto, Arial, sans-serif',
        'Cormorant': 'Cormorant, Georgia, serif',
        'Lora': 'Lora, Georgia, serif',
        'Merriweather': 'Merriweather, Georgia, serif',
        'Montserrat': 'Montserrat, Arial, sans-serif',
        'Noto Sans': '"Noto Sans", Arial, sans-serif',
        'Noto Serif': '"Noto Serif", Georgia, serif',
        'Open Sans': '"Open Sans", Arial, sans-serif',
        'PT Sans': '"PT Sans", Arial, sans-serif',
        'PT Serif': '"PT Serif", Georgia, serif'
    };
    return fontMap[fontName] || `"${fontName}", serif`;
};

const normalizeFontStyle = (style = '') => {
    const chars = Array.from(new Set(style.toLowerCase().split('').filter(char => ['b', 'i'].includes(char))));
    if (chars.length === 2) return 'bi';
    return chars.join('');
};

const blockStyle = computed(() => {
    return {
        position: 'absolute',
        top: mmToPixel(block.value.y) + 'px',
        left: mmToPixel(block.value.x) + 'px',
        width: mmToPixel(block.value.width) + 'px',
        'min-height': mmToPixel(block.value.height) + 'px',
    }
});

const textStyle = computed(() => {
    const fontStyle = normalizeFontStyle(block.value.font.style);

    return {
        width: '100%',
        height: '100%',
        'line-height': mmToPixel(block.value.height) + 'px',
        'text-align': block.value.align === 'C' ? 'center' : block.value.align === 'R' ? 'right' : 'left',
        'font-size': block.value.font.size + 'pt',
        'font-family': getFontFamily(block.value.font.family),
        'color': block.value.font.color ?? 'red',
        'font-weight': fontStyle.indexOf('b') > -1 ? 'bold' : 'normal',
        'font-style': fontStyle.indexOf('i') > -1 ? 'italic' : 'normal',
        cursor: contentEditable.value ? 'text' : 'move',
    }
});

</script>
<style scoped lang="scss">
.text-block-canvas {
    &.hoverBorder:hover {
        outline: 1px dashed #868686;
    }
    &.activeBlock {
        outline: 1px dashed #42b983;
        background: #42b98315;
    }
    &.editingBlock {
        outline: 2px solid #42b983;
        background: rgba(255, 255, 255, 0.85);
        box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.2);
    }
}

.text-content {
    overflow: hidden;
    white-space: pre-wrap;
    word-wrap: break-word;
    outline: none;
}

[contenteditable] {
    outline: 0px solid transparent;
    cursor: text;
}

$handle-size: 8px;

.resize-handle {
    position: absolute;
    width: $handle-size;
    height: $handle-size;
    background: #42b983;
    border: 1px solid #fff;
    border-radius: 1px;
    z-index: 10;

    &.handle-tl { top: -$handle-size/2; left: -$handle-size/2; cursor: nwse-resize; }
    &.handle-tr { top: -$handle-size/2; right: -$handle-size/2; cursor: nesw-resize; }
    &.handle-bl { bottom: -$handle-size/2; left: -$handle-size/2; cursor: nesw-resize; }
    &.handle-br { bottom: -$handle-size/2; right: -$handle-size/2; cursor: nwse-resize; }
    &.handle-t  { top: -$handle-size/2; left: calc(50% - #{$handle-size}/2); cursor: ns-resize; }
    &.handle-b  { bottom: -$handle-size/2; left: calc(50% - #{$handle-size}/2); cursor: ns-resize; }
    &.handle-l  { top: calc(50% - #{$handle-size}/2); left: -$handle-size/2; cursor: ew-resize; }
    &.handle-r  { top: calc(50% - #{$handle-size}/2); right: -$handle-size/2; cursor: ew-resize; }
}

.wildcard-dropdown {
    position: absolute;
    z-index: 1000;
    background: #fff;
    border: 1px solid #ced4da;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    max-height: 180px;
    overflow-y: auto;
    min-width: 160px;
}

.wildcard-item {
    padding: 5px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: normal;
    font-style: normal;
    color: #333;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    line-height: 1.4;

    &:hover,
    &.highlighted {
        background: #e9ecef;
    }

    .wildcard-value {
        font-family: monospace;
        color: #495057;
        font-weight: 600;
    }

    .wildcard-label {
        color: #868e96;
        font-size: 12px;
    }
}
</style>
